/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.TEMPLATE.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/eds/EdsTemplateListPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (eds_email_template + eds_email_template_tl for the localized
 *     subject / preheader / body preview, and eds_template_type_tl to resolve the
 *     template_type_code to a translated label), resolved against the ACTIVE locale
 *     (§8, §17.B4 — never a raw id/code reaches render; the type code is shown as a
 *     translated label),
 *   - the scalar edits (type / from name / from email / reply-to / active) route
 *     through the Common Routine via routedUpdate — §2 single write path; never a
 *     direct apiDb write.
 * It never re-renders the desktop grid; §16. The per-language subject / body HTML
 * live in eds_email_template_tl (edited on the desktop), so the body is shown
 * read-only here (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Mail } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { formatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'EDS.TEMPLATE.LIST';

interface MobileTemplateRow {
  template_id: string;
  internal_code: string;
  template_type_code: string;
  from_name_default: string;
  from_email_default: string;
  reply_to_email: string | null;
  is_active: 'Y' | 'N';
  updated_at: string;
  subject_line: string | null;
  preheader_text: string | null;
  body_html: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TypeOption {
  code: string;
  label: string;
}

interface TlRow {
  language_id: string;
  subject_line: string | null;
  preheader_text: string | null;
  body_html: string | null;
}

type RawTemplateRow = {
  template_id: string;
  internal_code: string;
  template_type_code: string;
  from_name_default: string;
  from_email_default: string;
  reply_to_email: string | null;
  is_active: 'Y' | 'N';
  updated_at: string;
  eds_email_template_tl?: TlRow[];
};

interface TypeTlRow {
  template_type_code: string;
  language_id: string;
  display_text: string;
}

export function MobileEdsTemplateListPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileTemplateRow[]>([]);
  // §17.B13 relation: the campaigns that use each template, as the desktop shows.
  const [campaignsByTemplate, setCampaignsByTemplate] = useState<Map<string, { campaign_id: string; code: string }[]>>(new Map());
  // §17.B4: template_type_code -> localized display_text from eds_template_type_tl.
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  // Full type list (from the lookup table) for the edit-sheet picker.
  const [typeCodes, setTypeCodes] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [typeFacet, setTypeFacet] = useState<string>('all');
  const [selected, setSelected] = useState<MobileTemplateRow | null>(null);
  const [editing, setEditing] = useState<MobileTemplateRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeLabel = (code: string): string => typeLabels[code] ?? code;

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_email_template')
        .select(
          'template_id, internal_code, template_type_code, from_name_default, from_email_default, reply_to_email, is_active, updated_at, eds_email_template_tl ( language_id, subject_line, preheader_text, body_html )',
        )
        .order('internal_code');
      if (e) throw e;

      const list: MobileTemplateRow[] = ((data ?? []) as RawTemplateRow[]).map((r) => {
        const tls = r.eds_email_template_tl ?? [];
        const exact = tls.find((tl) => tl.language_id === activeLocale);
        const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
        const chosen = exact ?? fb;
        return {
          template_id: r.template_id,
          internal_code: r.internal_code,
          template_type_code: r.template_type_code,
          from_name_default: r.from_name_default,
          from_email_default: r.from_email_default,
          reply_to_email: r.reply_to_email,
          is_active: r.is_active,
          updated_at: r.updated_at,
          subject_line: chosen?.subject_line ?? null,
          preheader_text: chosen?.preheader_text ?? null,
          body_html: chosen?.body_html ?? null,
        };
      });
      setRows(list);

      // §17.B4: resolve each template_type_code to its localized name.
      const { data: typeRows } = await apiDb
        .from('eds_template_type')
        .select('template_type_code')
        .order('sort_order');
      setTypeCodes(((typeRows ?? []) as { template_type_code: string }[]).map((r) => r.template_type_code));

      const { data: campRows } = await apiDb
        .from('eds_campaign')
        .select('campaign_id, internal_code, template_id');
      const byTemplate = new Map<string, { campaign_id: string; code: string }[]>();
      for (const c of (campRows ?? []) as { campaign_id: string; internal_code: string; template_id: string }[]) {
        const arr = byTemplate.get(c.template_id) ?? [];
        arr.push({ campaign_id: c.campaign_id, code: c.internal_code });
        byTemplate.set(c.template_id, arr);
      }
      setCampaignsByTemplate(byTemplate);

      const { data: typeTl } = await apiDb
        .from('eds_template_type_tl')
        .select('template_type_code, language_id, display_text');
      const byCode: Record<string, TypeTlRow[]> = {};
      for (const r of (typeTl ?? []) as TypeTlRow[]) (byCode[r.template_type_code] ??= []).push(r);
      const labels: Record<string, string> = {};
      for (const [code, tls] of Object.entries(byCode)) {
        const exact = tls.find((tl) => tl.language_id === activeLocale);
        const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
        labels[code] = exact?.display_text ?? fb?.display_text ?? code;
      }
      setTypeLabels(labels);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (type labels + subject/
  // body preview follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (typeFacet !== 'all' && r.template_type_code !== typeFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.from_name_default.toLowerCase().includes(q) ||
        r.from_email_default.toLowerCase().includes(q) ||
        (r.subject_line ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, typeFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.eds_templates.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  // §17.B4: type facet option labels resolve to the translated name, never the code.
  const typeFacets: { key: string; label: string }[] = useMemo(() => {
    const uniq = [...new Set(rows.map((r) => r.template_type_code).filter(Boolean))].sort();
    return [
      { key: 'all', label: t('mobile.eds_templates.type_all', 'All types') },
      ...uniq.map((code) => ({ key: code, label: typeLabels[code] ?? code })),
    ];
  }, [rows, typeLabels, t]);  

  const typeOptions: TypeOption[] = useMemo(
    () => typeCodes.map((code) => ({ code, label: typeLabels[code] ?? code })),
    [typeCodes, typeLabels],
  );

  const toggleActive = async (r: MobileTemplateRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'eds_email_template', 'template_id', r.template_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.eds_templates.title', 'Email Templates')}
        description={t('mobile.eds_templates.description', 'Reusable email templates, their sender identity and status.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.eds_templates.search', 'Search templates')}
          aria-label={t('mobile.eds_templates.search', 'Search templates')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={typeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => typeFacet === v}
        onSelect={(v) => setTypeFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.eds_templates.none', 'No templates match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.template_id}
              lead={<Mail />}
              leadTone="blue"
              title={r.internal_code}
              subtitle={`${typeLabel(r.template_type_code)} · ${r.from_email_default}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.eds_templates.detail', 'Template detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Mail size={20} /></span>
              <span>
                <div className="m-nm">{selected.internal_code}</div>
                <div className="m-em">{typeLabel(selected.template_type_code)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.eds_templates.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.eds_templates.type', 'Type')}
                value={<MobileStatusPill tone="info">{typeLabel(selected.template_type_code)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.eds_templates.from_name', 'From name')} value={selected.from_name_default} />
              <MobileDetailRow label={t('mobile.eds_templates.from_email', 'From email')} value={selected.from_email_default} />
              <MobileDetailRow
                label={t('mobile.eds_templates.reply_to', 'Reply-to')}
                value={selected.reply_to_email?.trim() ? selected.reply_to_email : t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.eds_templates.subject', 'Subject')}
                value={selected.subject_line?.trim() ? selected.subject_line : t('common.none', 'None')}
              />
              <MobileDetailRow label={t('mobile.eds_templates.updated', 'Updated')} value={formatDateTime(selected.updated_at, activeLocale)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'campaigns',
                    table: 'eds_campaign', pkColumn: 'campaign_id',
                    title: t('mobile.eds_templates.related.campaigns', 'Campaigns using this template'),
                    items: (campaignsByTemplate.get(selected.template_id) ?? [])
                      .map(c => ({ id: c.campaign_id, label: c.code })),
                  },
                ]}
              />
            </MobileContentCard>

            {/* Body preview — read-only source; the per-language HTML/plain body is
                edited on the desktop (§16). */}
            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.eds_templates.body', 'HTML body preview')}</label>
                <div className="m-formrow-ro" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '9rem', overflowY: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                  {selected.body_html?.trim()
                    ? selected.body_html
                    : t('mobile.eds_templates.no_body', 'No body for this locale.')}
                  <span className="m-formrow-hint"> · {t('mobile.eds_templates.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.eds_templates.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileEdsTemplateEditSheet
        session={session}
        row={editing}
        typeOptions={typeOptions}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar fields (Type + From name + From email +
// Reply-to + Active), saved through the Common Routine (routedUpdate) — §2. The
// code is immutable and the per-language subject / body live in
// eds_email_template_tl (edited on the desktop), so they are shown read-only /
// omitted here (mirrors MobileUserEditSheet / MobileEcmTemplateEditSheet).
// ---------------------------------------------------------------------------

interface MobileEdsTemplateEditSheetProps {
  session: AuthSession;
  row: MobileTemplateRow | null;
  typeOptions: TypeOption[];
  onClose: () => void;
  onSaved: () => void;
}

function MobileEdsTemplateEditSheet({ session, row, typeOptions, onClose, onSaved }: MobileEdsTemplateEditSheetProps) {
  const { t } = useTranslation();
  const [typeCode, setTypeCode] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different template is opened.
  useEffect(() => {
    if (!row) return;
    setTypeCode(row.template_type_code);
    setFromName(row.from_name_default);
    setFromEmail(row.from_email_default);
    setReplyTo(row.reply_to_email ?? '');
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    if (!fromName.trim()) { setError(t('mobile.eds_templates.from_name_required', 'From name is required.')); return; }
    if (!fromEmail.trim()) { setError(t('mobile.eds_templates.from_email_required', 'From email is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'eds_email_template', 'template_id', row.template_id, {
      template_type_code: typeCode,
      from_name_default: fromName.trim(),
      from_email_default: fromEmail.trim(),
      reply_to_email: replyTo.trim() || null,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.eds_templates.edit_sheet', 'Edit template')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Mail size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.eds_templates.edit_title', 'Edit template')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.eds_templates.code', 'Code')}</label>
                <div className="m-formrow-ro">
                  {row.internal_code}
                  <span className="m-formrow-hint"> · {t('mobile.eds_templates.code_immutable', 'cannot be changed')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-eds-type">{t('mobile.eds_templates.type', 'Type')}</label>
                <select id="m-eds-type" className="m-sel" value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
                  {typeOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-eds-from-name">{t('mobile.eds_templates.from_name', 'From name')}</label>
                <input
                  id="m-eds-from-name"
                  className="m-inp"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-eds-from-email">{t('mobile.eds_templates.from_email', 'From email')}</label>
                <input
                  id="m-eds-from-email"
                  className="m-inp"
                  type="email"
                  inputMode="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-eds-reply-to">{t('mobile.eds_templates.reply_to', 'Reply-to')}</label>
                <input
                  id="m-eds-reply-to"
                  className="m-inp"
                  type="email"
                  inputMode="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder={t('mobile.eds_templates.reply_to_optional', 'Optional')}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.eds_templates.active_hint', 'Available for sending')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.eds_templates.content', 'Subject & body')}</label>
                <div className="m-formrow-ro">
                  {row.subject_line?.trim() ? row.subject_line : t('mobile.eds_templates.no_subject', 'No subject for this locale')}
                  <span className="m-formrow-hint"> · {t('mobile.eds_templates.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
